import { EventAvailable, EventBusy, Topic } from "@mui/icons-material";
import Warehouse from "@mui/icons-material/Warehouse";
import { Chip, CircularProgress, LinearProgress, Tooltip, Typography } from "@mui/material";
import { cloneDeep, debounce } from 'lodash';
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Api from "../../Api";
import { IContract, IMaterial } from "../../interfaces";
import { BreadcrumbBar } from "../Breadcrumb";
import { ExpandableContainer } from "../Shared/ExpandableContainer";
import FormNavigation from "../Shared/FormNavigation";
import { ContractActions } from "./ContractActions";
import { ContractDetailForm } from "./ContractDetail";
import "./index.scss";
import { MaterialInformationForm } from "./MaterialInformation";


export const Contract: React.FC = ({
    ...props
}) => {

    const [contract, setContract] = useState<IContract>();
    const [contractActive, setContractActive] = useState<boolean | null>(null);
    const [linkedSR, setLinkedSR] = useState<[{ storageRequestId: string, reference: string }] | null>(null);

    const [detailsValid, setDetailsValid] = useState<boolean>(false);
    const [materials, setMaterials] = useState<IMaterial[]>([]);
    const [bread, setBread] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);
    const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);

    const { cId } = useParams();

    const navigate = useNavigate();

    useEffect(() => {

        let requestedId: any = cId;
        if (requestedId) setBread("Update");
        else {
            setBread("Create");
            requestedId = null;
        }

        //when opening an existing request we will pass
        //in the id on the fetch request.

        fetchFormData(requestedId).then((data: any) => {
            populateForm(data);
        });

    }, []);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        validateDetails();
    }, [materials, contract])

    const fetchFormData = async (id: string | null | undefined) => {

        if (id) {

            return Api.Contracts.get(id);

        } else return Api.Contracts.create().then((data) => {

            //update the url so page reloads dont cause
            //multiple requests to be created

            var sr = data as IContract;
            window.history.pushState(
                "Update",
                "Update Contract",
                "/contract/update/" + sr.contractId);

            return data;
        });

    }

    const populateForm = (data: any) => {

        if (data) {
            setContract(data);
            setMaterials(data.materials);
        }

        if (data.storageRequests) {
            setLinkedSR(data.storageRequests.map((x: any) => {
                return {
                    storageRequestId: x.storageRequestId,
                    reference: x.storageRequestReference
                }
            }));
        }

        if (data.statusId === 3) {
            if (new Date() > new Date(data.endDate))
                setContractActive(false);
            else if (new Date() > new Date(data.startDate))
                setContractActive(true);
        }

        setLoading(false);
    }

    const onUpdateMaterials = (newMaterials: IMaterial[] | null | undefined) => {

        if (newMaterials && contract) {
            setUnsavedChanges(true);
            let updatedContract: IContract = cloneDeep(contract);
            updatedContract.materials = newMaterials;
            handleSaveDebounced(updatedContract);
        }
    }

    const onUpdateMaterial = async (material: IMaterial | null | undefined, split?: IMaterial) => {

        let newMaterials = cloneDeep(materials);

        if (material) {
            await Api.Materials.update(material).then(() => {

                newMaterials = newMaterials.filter(x =>
                    x.materialId !== material.materialId);

                newMaterials.push(material);

            }).catch((e) => {
                console.log("error", e);
            });
        }

        if (split) {
            await Api.Materials.create(split).then(() => {
                newMaterials.push(split);
            });
        }

        setMaterials(newMaterials);

    }

    const onImportComplete = (imported: IMaterial[]) => {

        let newMaterials = [ ...imported, ...cloneDeep(materials) ];
        setMaterials(newMaterials);
    }

    const handleSave = (model: IContract) => {
        Api.Contracts.save(model).then((response) => {
            let updateResponse = response as IContract;

            setMaterials(updateResponse.materials);
        });
        setUnsavedChanges(false);
    }
    const handleSaveDebounced = useRef(debounce(handleSave, 1000)).current;

    const onUpdateContractDetail = (contract: IContract) => {
        validateDetails();

        setContract(prev => {
            setUnsavedChanges(true);
            return contract
        });

        handleSaveDebounced(contract);
    }

    const validateDetails = () => {

        let form: HTMLFormElement = document.getElementById("contract-details-form") as HTMLFormElement;
        if (form) {

            setDetailsValid(form.checkValidity());


        }


        //every material must have an expected return condition
        materials.forEach(material => {

            if (!material.expectedReturnState)
                setDetailsValid(false);
        })

    }

    const onSubmit = async () => {
        setLoading(true);
        return Api.Contracts.submit(contract?.contractId as string).then(async (r: any) => {

            fetchFormData(contract?.contractId).then((data: any) => {
                populateForm(data);
                if (r.storageRequest)
                    setLinkedSR([{
                        storageRequestId: r.storageRequest.storageRequestId,
                        reference: r.storageRequest.storageRequestReference ?? "NO-REF"
                    }]);
                setLoading(false);
                navigate("/contract");
            });

        });
    }

    const onDelete = async () => {
        setLoading(true);
        return Api.Contracts.delete(contract?.contractId as string).then(async (r: any) => {
            navigate("/contract");
        });
    }

    const onApprove = async () => {
        setLoading(true);
        return Api.Contracts.approve(contract?.contractId as string).then(async (r) => {

            fetchFormData(contract?.contractId).then((data: any) => {
                populateForm(data);
                setLoading(false);

            });

        });
    }

    const onComplete = async () => {
        setLoading(true);
        return Api.StorageRequests.complete(contract?.contractId as string).then(async (r) => {

            fetchFormData(contract?.contractId).then((data: any) => {
                populateForm(data);
                setLoading(false);
            });

        });
    }

    const onReject = async (reason: string) => {
        setLoading(true);

        return Api.Contracts.reject(contract?.contractId as string, reason).then(async (r) => {

            fetchFormData(contract?.contractId).then((data: any) => {
                populateForm(data);
                setLoading(false);

            });

        });
    }

    const onClose = async () => {
        setLoading(true);

        return Api.Contracts.close(contract?.contractId as string).then(async (r) => {

            fetchFormData(contract?.contractId).then((data: any) => {
                populateForm(data);
                setLoading(false);

            });

        });
    }

    const getStatus = () => { return contract?.status ?? 0 }

    const getStatusLabel = () => {

        if (loading)
            return <CircularProgress size="2rem" />;

        switch (getStatus()) {
            case 1:
                return <Chip
                    label={"Draft Contract / Lease"}
                    color="default"
                    variant="filled"
                />;
            case 2:
                return <Chip
                    label={"Awaiting Approval - Open"}
                    color="primary"
                    variant="outlined"
                />;
            case 3: return <Chip
                label={"Approved - Open"}
                color="primary"
                variant="outlined"
            />;
            case 4: return <Chip
                label={"Rejected - Open"}
                color="primary"
                variant="outlined"
            />;
            case 5: return <Chip
                label={"Rejected - Closed"}
                color="default"
                variant="filled"
            />;
            case 6: return <Chip
                label={"Approved - Live"}
                color="default"
                variant="filled"
            />;
            case 7: return <Chip
                label={"Ended"}
                color="default"
                variant="filled"
            />;
            default:
                return <Chip
                    label={"..."}
                    style={{ minWidth: '4rem' }}
                    color="primary"
                    variant="outlined"
                />;
        }

    }

    const getLinkLabel = () => {

        if (linkedSR)
            return <>

                {linkedSR.map((x: any, i: number) => (
                    < Tooltip key={i} title="Go to storage request" placement="right" >
                        <Chip
                            sx={{ ml: 2, pl: 0.5, cursor: 'pointer' }}
                            icon={<Warehouse />}
                            label={x.reference}
                            color="primary"
                            variant="filled"
                            onClick={() => navigate('/storagerequest/update/' + x.storageRequestId)}

                        />
                    </Tooltip>
                ))}


            </>;

        return <></>;
    }

    const getActiveLabel = () => {

        if (contractActive !== null) {
            return <Chip
                sx={{ ml: 2, pl: .5, float: "right" }}
                color={contractActive ? "success" : "error"}
                icon={contractActive ? <EventAvailable /> : <EventBusy />}
                label={contractActive ? "Active" : "Expired"}
            />
        } else return <></>;
    }


    const isReadonly = () => {
        return ![1, 4].includes(getStatus());
    }

    return (
        <ExpandableContainer>
            <BreadcrumbBar
                current={bread}
                previous={[{
                    name: "Contract / Lease List",
                    href: "/contract"
                }]}
            />
            <Typography variant="h4" sx={{ py: 3 }}>{contract ?
                <>
                    <Topic fontSize="inherit" sx={{ opacity: .8 }} color="primary" />
                    {contract.reference}
                    <FormNavigation />
                </> : <CircularProgress />} </Typography>

            {getStatusLabel()}
            {getLinkLabel()}
            {getActiveLabel()}


            {contract && <form id="contract-details-form">
                <div className="form-section">
                    <ContractDetailForm
                        contract={contract}
                        updateContract={onUpdateContractDetail}
                        readonly={isReadonly()}
                        saving={unsavedChanges}
                    />
                </div>
            </form>
            }

            <div className="form-section">
                <MaterialInformationForm
                    entityId={contract?.contractId ?? undefined}
                    status={getStatus()}
                    materials={materials}
                    onUpdateMaterials={onUpdateMaterials}
                    onUpdateMaterial={onUpdateMaterial}
                    onImportComplete={onImportComplete}
                    readonly={isReadonly()}
                    readonlyMaterialActions={false}
                    saving={unsavedChanges}
                />
            </div>

            <div className="form-section">
                {loading ? <LinearProgress /> :
                    <ContractActions
                        materials={materials}
                        status={getStatus()}
                        formValid={detailsValid}
                        {...{
                            onSubmit,
                            onDelete,
                            onApprove,
                            onReject,
                            onClose,
                            onComplete
                        }}
                        readonly={false}
                    />
                }
            </div>

        </ExpandableContainer>
    );
}